import { cur } from '@/lib/currency';
import { useEffect, useRef, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, DollarSign, TrendingUp, TrendingDown , Handshake, FileText, Download, Banknote, EyeOff, Eye, History, AlertTriangle } from 'lucide-react';
import BankPaymentDialog from '@/components/BankPaymentDialog';
import { exportPartnerStatementToPDF } from '@/lib/pdfExport';
import api from '@/lib/api';
import { useBranchStore, useFeatureFlagsStore } from '@/stores';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { BranchFilter } from '@/components/BranchFilter';
import BankAccountsEditor from '@/components/BankAccountsEditor';
import { SalaryHistoryList } from '@/components/SalaryHistoryList';
import { SalaryStatusDialog } from '@/components/SalaryStatusDialog';
import { SalaryDeductionCard } from '@/components/SalaryDeductionCard';
import { CalendarDays } from 'lucide-react';
import { localToday } from '@/lib/dateRange';

const TXN_TYPES = [
  { value: 'investment', label: 'Investment / Deposit', color: 'bg-success/20 text-success' },
  { value: 'withdrawal', label: 'Withdrawal', color: 'bg-error/20 text-error' },
  { value: 'profit_share', label: 'Profit Share', color: 'bg-warning/20 text-warning' },
  { value: 'expense', label: 'Expense', color: 'bg-info/20 text-info' },
  { value: 'salary', label: 'Salary Payment', color: 'bg-orange-100 text-orange-700' },
  { value: 'advance', label: 'Advance / Loan', color: 'bg-warning/20 text-warning' },
  { value: 'loan_repayment', label: 'Loan Repayment', color: 'bg-success/20 text-success' },
  { value: 'other', label: 'Other', color: 'bg-muted text-foreground' },
];

export default function PartnersPage() {
  const [partners, setPartners] = useState([]);
  // iter593 — Tenant feature flag: hide partner entries in expense /
  // banking flows by default. Owners toggle this from the banner below.
  const partnersEnabled = useFeatureFlagsStore(s => s.flags.enable_partner_entries);
  const setFlag = useFeatureFlagsStore(s => s.setFlag);
  const [togglingFlag, setTogglingFlag] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const { branches, fetchBranches: _fetchBr } = useBranchStore();
  const [loading, setLoading] = useState(true);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [showTxnDialog, setShowTxnDialog] = useState(false);
  // Stable idempotency key for a single txn submission. Reused across retries
  // of the SAME submit; cleared on success and when the dialog (re)opens so a
  // genuinely new transaction always gets a fresh key.
  const txnReqIdRef = useRef(null);
  const [showPaySalaryDialog, setShowPaySalaryDialog] = useState(false);
  const [payingPartner, setPayingPartner] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [branchFilter, setBranchFilter] = useState([]);
  const [partnerData, setPartnerData] = useState({ name: '', phone: '', email: '', share_percentage: '', salary: '', notes: '', document_id: '', document_expiry: '', bank_accounts: [], alt_names: [] });
  const [salaryMeta, setSalaryMeta] = useState({ effective_date: '', reason: '' });
  const [confirmOverpay, setConfirmOverpay] = useState(false);
  const [txnVisible, setTxnVisible] = useState(50);
  const [salaryPayData, setSalaryPayData] = useState({ type: 'salary', amount: '', payment_mode: 'cash', branch_id: '', period_month: new Date().getMonth(), period_year: new Date().getFullYear(), date: localToday(), comments: '' });
  // iter778 — smart pre-payment alerts for partner salary
  const [partnerAlerts, setPartnerAlerts] = useState([]);
  const [partnerDeductions, setPartnerDeductions] = useState(null);
  const [statusPartner, setStatusPartner] = useState(null);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const periodString = (m, y) => `${MONTHS[m]} ${y}`;
  useEffect(() => {
    if (!showPaySalaryDialog || !payingPartner || salaryPayData.type !== 'salary') {
      setPartnerAlerts([]); setPartnerDeductions(null);
      return;
    }
    const period = periodString(salaryPayData.period_month, salaryPayData.period_year);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/partner-payment-alerts', { params: { partner_id: payingPartner.id, period, amount: parseFloat(salaryPayData.amount) || 0 } });
        setPartnerAlerts(data.alerts || []);
        setPartnerDeductions(data.deductions || null);
      } catch { setPartnerAlerts([]); setPartnerDeductions(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [showPaySalaryDialog, payingPartner, salaryPayData.type, salaryPayData.period_month, salaryPayData.period_year, salaryPayData.amount]);  const [txnData, setTxnData] = useState({ partner_id: '', transaction_type: 'investment', amount: '', payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', description: '', date: localToday() });
  const [editTxn, setEditTxn] = useState(null);
  const saveEditTxn = async () => {
    if (!editTxn) return;
    try {
      await api.put(`/partner-transactions/${editTxn.id}`, {
        amount: parseFloat(editTxn.amount),
        date: editTxn.date,
        payment_mode: editTxn.payment_mode,
        description: editTxn.description,
      });
      toast.success('Transaction corrected. Linked expense & ledger updated.');
      setEditTxn(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  // Partner Statement of Account
  const _currentYear = new Date().getFullYear();
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [stmtPartner, setStmtPartner] = useState(null);
  const [stmtRange, setStmtRange] = useState({ start_date: `${_currentYear}-01-01`, end_date: `${_currentYear}-12-31` });
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtPreview, setStmtPreview] = useState(null);

  // Bulk "Pay All Salaries"
  const [showPayAllDialog, setShowPayAllDialog] = useState(false);
  // "Pay from Bank" shortcut state
  const [showBankPay, setShowBankPay] = useState(false);
  const [bankPaySeed, setBankPaySeed] = useState(null);
  const [payAllSettings, setPayAllSettings] = useState({
    period_month: new Date().getMonth(),
    period_year: new Date().getFullYear(),
    date: localToday(),
    payment_mode: 'bank',
    branch_id: '',
  });
  const [payAllRows, setPayAllRows] = useState({}); // { partner_id: { include: bool, amount: str } }
  const [payAllLoading, setPayAllLoading] = useState(false);

  const openPayAll = () => {
    const init = {};
    for (const p of partners) {
      if ((p.salary || 0) > 0) {
        init[p.id] = { include: true, amount: String(p.salary) };
      }
    }
    setPayAllRows(init);
    setShowPayAllDialog(true);
  };

  const submitPayAll = async () => {
    const period = periodString(payAllSettings.period_month, payAllSettings.period_year);
    const overrides = Object.entries(payAllRows).map(([partner_id, r]) => ({
      partner_id,
      include: r.include,
      amount: r.amount ? parseFloat(r.amount) : undefined,
    }));
    setPayAllLoading(true);
    try {
      const res = await api.post('/partners/pay-all-salaries', {
        period,
        payment_mode: payAllSettings.payment_mode,
        date: payAllSettings.date,
        branch_id: payAllSettings.branch_id || null,
        overrides,
        skip_duplicates: true,
      });
      const d = res.data;
      const paid = d.results.filter(r => r.status === 'paid').length;
      const dup = d.results.filter(r => r.status === 'skipped_duplicate').length;
      const err = d.results.filter(r => r.status === 'error').length;
      const msg = `Paid ${paid} · Total ${cur()} ${d.total_amount.toFixed(2)}` +
        (dup ? ` · Skipped ${dup} duplicate${dup > 1 ? 's' : ''}` : '') +
        (err ? ` · ${err} error${err > 1 ? 's' : ''}` : '');
      if (err > 0) toast.error(msg); else toast.success(msg);
      setShowPayAllDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk payment failed');
    } finally {
      setPayAllLoading(false);
    }
  };

  const openStatement = (p) => {
    setStmtPartner(p);
    const y = new Date().getFullYear();
    setStmtRange({ start_date: `${y}-01-01`, end_date: `${y}-12-31` });
    setStmtPreview(null);
    setShowStatementDialog(true);
  };

  const fetchStatement = async () => {
    if (!stmtPartner) return null;
    setStmtLoading(true);
    try {
      const res = await api.get(`/partners/${stmtPartner.id}/statement`, { params: stmtRange });
      setStmtPreview(res.data);
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load statement');
      return null;
    } finally {
      setStmtLoading(false);
    }
  };

  const downloadStatementPDF = async () => {
    const data = stmtPreview || await fetchStatement();
    if (data) exportPartnerStatementToPDF(data);
  };

  useEffect(() => { fetchData(); }, []);

  // iter839 — Deep-link from Payroll Salary Status "Pay Rest" button
  // (?payRemaining=1&partnerId=X&period=YYYY-MM&amount=Y).
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('payRemaining') !== '1') return;
    const pid = url.searchParams.get('partnerId');
    const period = url.searchParams.get('period') || ''; // format YYYY-MM
    const amount = url.searchParams.get('amount') || '';
    if (!pid) return;
    (async () => {
      try {
        const { data: parts } = await api.get('/partners');
        const p = (parts || []).find((x) => x.id === pid);
        if (!p) { toast.error('Partner not found'); return; }
        setPayingPartner(p);
        // Convert YYYY-MM → { period_month:int (0-11), period_year:int }
        let period_month = new Date().getMonth();
        let period_year = new Date().getFullYear();
        if (/^\d{4}-\d{2}$/.test(period)) {
          const [y, m] = period.split('-').map(Number);
          period_year = y;
          period_month = m - 1;
        }
        setSalaryPayData({
          type: 'salary',
          amount: String(amount || (p.salary || 0)),
          payment_mode: 'cash',
          branch_id: p.branch_id || '',
          period_month,
          period_year,
          date: localToday(),
          comments: `Remaining balance for ${period}`,
        });
        setShowPaySalaryDialog(true);
        url.searchParams.delete('payRemaining');
        url.searchParams.delete('partnerId');
        url.searchParams.delete('period');
        url.searchParams.delete('amount');
        window.history.replaceState({}, '', url.toString());
      } catch { /* ignore */ }
    })();
  }, []);
  const fetchData = async () => {
    try {
      const [pR, tR, bR] = await Promise.all([api.get('/partners'), api.get('/partner-transactions'), Promise.resolve({ data: [] })]);
      setPartners(pR.data); setTransactions(tR.data); // branches from store
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    const salaryChanged = editingPartner && Math.abs((parseFloat(partnerData.salary) || 0) - (parseFloat(editingPartner.salary) || 0)) > 0.001;
    if (salaryChanged && !salaryMeta.effective_date) { toast.error('Effective Date is required when changing salary'); return; }
    try {
      const payload = { ...partnerData, share_percentage: parseFloat(partnerData.share_percentage) || 0, salary: parseFloat(partnerData.salary) || 0, document_expiry: partnerData.document_expiry ? new Date(partnerData.document_expiry).toISOString() : null, ...(salaryChanged ? { salary_effective_date: salaryMeta.effective_date, salary_change_reason: salaryMeta.reason } : {}) };
      if (editingPartner) { await api.put(`/partners/${editingPartner.id}`, payload); toast.success('Updated'); }
      else { await api.post('/partners', payload); toast.success('Partner added'); }
      setShowPartnerDialog(false); setPartnerData({ name: '', phone: '', email: '', share_percentage: '', salary: '', notes: '', document_id: '', document_expiry: '', bank_accounts: [], alt_names: [] }); setEditingPartner(null); setSalaryMeta({ effective_date: '', reason: '' }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddTxn = async (e) => {
    e.preventDefault();
    // Reuse the same key for retries of this submission; generate once.
    if (!txnReqIdRef.current) {
      txnReqIdRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    }
    try {
      await api.post('/partner-transactions', { ...txnData, amount: parseFloat(txnData.amount), branch_id: txnData.branch_id || null, expense_for_branch_id: txnData.expense_for_branch_id || null, date: `${txnData.date}T${new Date().toTimeString().slice(0,8)}`, client_request_id: txnReqIdRef.current });
      toast.success('Transaction recorded'); txnReqIdRef.current = null; setShowTxnDialog(false); setTxnData({ partner_id: '', transaction_type: 'investment', amount: '', payment_mode: 'cash', branch_id: '', expense_for_branch_id: '', description: '', date: localToday() }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  const totalInvested = partners.reduce((s, p) => s + (p.total_invested || 0), 0);
  const totalWithdrawn = partners.reduce((s, p) => s + (p.total_withdrawn || 0), 0);
  const totalOpsPaid = partners.reduce((s, p) => s + (p.total_ops_paid || 0), 0);
  const totalLoanBalance = partners.reduce((s, p) => s + (p.loan_balance || 0), 0);
  const filteredTxns = transactions.filter(t => branchFilter.length === 0 || branchFilter.includes(t.branch_id) || !t.branch_id);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">

                <Handshake size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="partners-title">Partners</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Business partner management</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm">Track partner investments, withdrawals & balances</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <BranchFilter onChange={setBranchFilter} />
            <Dialog open={showPartnerDialog} onOpenChange={(o) => { setShowPartnerDialog(o); if (!o) { setEditingPartner(null); setPartnerData({ name: '', phone: '', email: '', share_percentage: '', salary: '', notes: '', document_id: '', document_expiry: '', bank_accounts: [], alt_names: [] }); setSalaryMeta({ effective_date: '', reason: '' }); } }}>
              <DialogTrigger asChild><Button className="rounded-xl"><Plus size={16} className="mr-2" />Add Partner</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle className="font-outfit">{editingPartner ? 'Edit' : 'Add'} Partner</DialogTitle></DialogHeader>
                <form onSubmit={handleAddPartner} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Name *</Label><Input value={partnerData.name} onChange={(e) => setPartnerData({ ...partnerData, name: e.target.value })} required /></div>
                    <div><Label>Share %</Label><Input type="number" step="0.1" value={partnerData.share_percentage} onChange={(e) => setPartnerData({ ...partnerData, share_percentage: e.target.value })} placeholder="0" /></div>
                    <div><Label>Monthly Salary</Label><Input type="number" step="0.01" value={partnerData.salary} onChange={(e) => setPartnerData({ ...partnerData, salary: e.target.value })} placeholder="SR 0" /></div>
                    {editingPartner && Math.abs((parseFloat(partnerData.salary) || 0) - (parseFloat(editingPartner.salary) || 0)) > 0.001 && (<>
                      <div><Label>Salary Effective Date *</Label><Input type="date" value={salaryMeta.effective_date} data-testid="partner-salary-effective-date" onChange={(e) => setSalaryMeta({ ...salaryMeta, effective_date: e.target.value })} /></div>
                      <div><Label>Change Reason</Label><Input value={salaryMeta.reason} data-testid="partner-salary-change-reason" onChange={(e) => setSalaryMeta({ ...salaryMeta, reason: e.target.value })} placeholder="e.g. Annual raise" /></div>
                    </>)}
                    <div><Label>Phone</Label><Input value={partnerData.phone} onChange={(e) => setPartnerData({ ...partnerData, phone: e.target.value })} /></div>
                    <div><Label>Email</Label><Input value={partnerData.email} onChange={(e) => setPartnerData({ ...partnerData, email: e.target.value })} /></div>
                    <div><Label>ID / Iqama Number</Label><Input value={partnerData.document_id} data-testid="partner-document-id" onChange={(e) => setPartnerData({ ...partnerData, document_id: e.target.value })} placeholder="ID / Iqama No." /></div>
                    <div><Label>ID / Iqama Expiry</Label><Input type="date" value={partnerData.document_expiry} data-testid="partner-document-expiry" onChange={(e) => setPartnerData({ ...partnerData, document_expiry: e.target.value })} /></div>
                  </div>
                  <BankAccountsEditor
                    value={partnerData.bank_accounts}
                    onChange={(arr) => setPartnerData({ ...partnerData, bank_accounts: arr })}
                    testidPrefix="partner-bank"
                  />
                  <Button type="submit" className="rounded-xl">{editingPartner ? 'Update' : 'Add'} Partner</Button>
                </form>
                {editingPartner && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-semibold mb-2 flex items-center gap-1.5"><History size={13} /> Salary History</p>
                    <SalaryHistoryList entityType="partner" entityId={editingPartner.id} refreshKey={showPartnerDialog ? 1 : 0} />
                  </div>
                )}
              </DialogContent>
            </Dialog>
            {partners.some(p => (p.salary || 0) > 0) && (
              <Button
                variant="outline"
                className="rounded-xl bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700 dark:bg-orange-950/20 dark:border-orange-800/40 dark:text-orange-300"
                onClick={openPayAll}
                data-testid="pay-all-salaries-btn"
              >
                <DollarSign size={16} className="mr-2" />Pay All Salaries
              </Button>
            )}
            <Dialog open={showTxnDialog} onOpenChange={(o) => { if (o) txnReqIdRef.current = null; setShowTxnDialog(o); }}>
              <DialogTrigger asChild><Button variant="outline" className="rounded-xl"><Plus size={16} className="mr-2" />Investment / Withdrawal</Button></DialogTrigger>
              <DialogContent><DialogHeader><DialogTitle className="font-outfit">Partner Transaction</DialogTitle></DialogHeader>
                <form onSubmit={handleAddTxn} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Partner *</Label><Select value={txnData.partner_id || "none"} onValueChange={(v) => setTxnData({ ...txnData, partner_id: v === "none" ? "" : v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Type *</Label><Select value={txnData.transaction_type} onValueChange={(v) => setTxnData({ ...txnData, transaction_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TXN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Amount *</Label><Input type="number" step="0.01" value={txnData.amount} onChange={(e) => setTxnData({ ...txnData, amount: e.target.value })} required /></div>
                    <div><Label>Mode</Label><Select value={txnData.payment_mode} onValueChange={(v) => setTxnData({ ...txnData, payment_mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent></Select></div>
                    <div><Label>Branch</Label><Select value={txnData.branch_id || "none"} onValueChange={(v) => setTxnData({ ...txnData, branch_id: v === "none" ? "" : v })}><SelectTrigger><SelectValue placeholder="Overall" /></SelectTrigger><SelectContent><SelectItem value="none">Overall</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Transaction For (Owner)</Label><Select value={txnData.expense_for_branch_id || "same"} onValueChange={(v) => setTxnData({ ...txnData, expense_for_branch_id: v === "same" ? "" : v })}><SelectTrigger data-testid="partner-owner-branch-select"><SelectValue placeholder="Same as paying branch" /></SelectTrigger><SelectContent><SelectItem value="same">Same as paying branch</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Date</Label><Input type="date" value={txnData.date} onChange={(e) => setTxnData({ ...txnData, date: e.target.value })} /></div>
                  </div>
                  <div><Label>Description</Label><Input value={txnData.description} onChange={(e) => setTxnData({ ...txnData, description: e.target.value })} /></div>
                  <Button type="submit" className="rounded-xl">Record Transaction</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1.5"><TrendingUp size={12} />Capital Invested</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-success">{cur()} {totalInvested.toFixed(2)}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1.5"><TrendingDown size={12} />Capital Withdrawn</CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-error">{cur()} {totalWithdrawn.toFixed(2)}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Net Capital</CardTitle></CardHeader><CardContent><div className={`text-xl font-bold ${totalInvested - totalWithdrawn >= 0 ? 'text-success' : 'text-error'}`}>{cur()} {(totalInvested - totalWithdrawn).toFixed(2)}</div></CardContent></Card>
          <Card className="border-border bg-orange-50/50 dark:bg-orange-950/10"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground flex items-center gap-1.5"><DollarSign size={12} />Paid to Partners <span className="text-[9px] text-muted-foreground">(Ops)</span></CardTitle></CardHeader><CardContent><div className="text-xl font-bold text-orange-600">{cur()} {totalOpsPaid.toFixed(2)}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Loan Outstanding</CardTitle></CardHeader><CardContent><div className={`text-xl font-bold ${totalLoanBalance > 0 ? 'text-warning' : 'text-muted-foreground'}`}>{cur()} {totalLoanBalance.toFixed(2)}</div></CardContent></Card>
        </div>

        {/* iter593 — Tenant-wide Partner-entries toggle. Default OFF: hides
            the "Partner" option in Multi-Person Expense dialog, Banking
            Hub / Quick-Link link-to chips, and similar places. Toggle ON
            re-exposes them everywhere. Read-only display for non-admins. */}
        <Card className={`border ${partnersEnabled ? 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/10' : 'border-stone-300 bg-stone-50/60 dark:bg-stone-900/30'}`}>
          <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${partnersEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}>
                {partnersEnabled ? <Eye size={16} /> : <EyeOff size={16} />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium" data-testid="partner-flag-headline">
                  {partnersEnabled ? 'Partner entries are visible in expenses & banking' : 'Partner entries are hidden in expenses & banking'}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {partnersEnabled
                    ? 'Multi-Person Expense, Banking Hub linker and similar flows show the "Partner" option.'
                    : 'Locked — enable requires the admin unlock code. Existing partner-tagged data stays untouched.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch
                checked={partnersEnabled}
                disabled={togglingFlag}
                onCheckedChange={async (val) => {
                  // iter594 — Turning ON is code-gated. OFF is always free.
                  let extra = {};
                  if (val) {
                    const code = window.prompt('Enter unlock code to enable Partner entries:');
                    if (code === null) return; // cancelled
                    if (!code) { toast.error('Unlock code is required'); return; }
                    extra = { unlock_code: code };
                  }
                  setTogglingFlag(true);
                  try {
                    await setFlag('enable_partner_entries', val, extra);
                    toast.success(val ? 'Partner entries enabled' : 'Partner entries hidden');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to update');
                  } finally {
                    setTogglingFlag(false);
                  }
                }}
                data-testid="toggle-partner-entries"
              />
              <span className="text-xs font-medium select-none">{partnersEnabled ? 'ON' : 'OFF'}</span>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map(p => (
            <Card key={p.id} className="border-border stat-card">
              <CardHeader><CardTitle className="font-outfit flex justify-between items-start">
                <div><span>{p.name}</span>{p.share_percentage > 0 && <Badge className="ml-2 bg-primary/20 text-primary">{p.share_percentage}%</Badge>}</div>
                <div className="flex gap-1">
                  {p.salary > 0 && <Button size="sm" variant="outline" onClick={() => { setPayingPartner(p); setSalaryPayData({ type: 'salary', amount: p.salary.toString(), payment_mode: 'cash', branch_id: '', period_month: new Date().getMonth(), period_year: new Date().getFullYear(), date: localToday(), comments: '' }); setShowPaySalaryDialog(true); }} className="h-7 text-xs"><DollarSign size={12} className="mr-1" />Pay</Button>}
                  <Button size="sm" variant="outline" onClick={() => setStatusPartner(p)} className="h-7 text-xs" data-testid={`partner-salary-status-${p.id}`}><CalendarDays size={12} className="mr-1" />Status</Button>
                  <Button size="sm" variant="outline"
                    onClick={() => { setBankPaySeed({ payee_type: 'partner', payee_id: p.id, payee_label: p.name }); setShowBankPay(true); }}
                    data-testid={`pay-bank-partner-${p.id}`}
                    className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                    title="Pay this partner from a bank account"
                  ><Banknote size={12} className="mr-1" />Bank</Button>
                  <Button size="sm" variant="outline" onClick={() => openStatement(p)} className="h-7 text-xs" data-testid={`partner-statement-btn-${p.id}`}><FileText size={12} className="mr-1" />Statement</Button>
                  <Button size="sm" variant="ghost" onClick={() => {                   setEditingPartner(p);
                  setPartnerData({
                    name: p.name, phone: p.phone || '', email: p.email || '',
                    share_percentage: p.share_percentage || '', salary: p.salary || '', notes: p.notes || '',
                    document_id: p.document_id || '', document_expiry: p.document_expiry ? String(p.document_expiry).slice(0, 10) : '',
                    bank_accounts: p.bank_accounts || [], alt_names: p.alt_names || [],
                  });
                  setShowPartnerDialog(true);
                }} className="h-7" data-testid={`edit-${p.id}`}><Edit size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={async () => { if (window.confirm('Delete?')) { await api.delete(`/partners/${p.id}`); fetchData(); } }} className="h-7 text-error"><Trash2 size={14} /></Button>
                </div>
              </CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(p.document_id || p.document_expiry) && (() => {
                  const days = p.document_expiry ? Math.floor((new Date(p.document_expiry) - new Date()) / 86400000) : null;
                  const near = days != null && days <= 30;
                  return (
                    <div className={`flex justify-between items-center p-2 rounded text-xs ${near ? 'bg-error/10' : 'bg-muted/50'}`} data-testid={`partner-id-info-${p.id}`}>
                      <span className="flex items-center gap-1.5 text-muted-foreground"><FileText size={12} />ID / Iqama{p.document_id ? `: ${p.document_id}` : ''}</span>
                      {p.document_expiry && (
                        <span className={`font-medium ${near ? 'text-error' : 'text-foreground'}`}>
                          {new Date(p.document_expiry).toLocaleDateString()}{days != null && (days < 0 ? ` · ${Math.abs(days)}d expired` : near ? ` · ${days}d left` : '')}
                        </span>
                      )}
                    </div>
                  );
                })()}
                {p.salary > 0 && <div className="flex justify-between p-2 bg-muted/50 rounded"><span className="text-xs">Monthly Salary</span><span className="text-sm font-bold">{cur()} {p.salary.toFixed(2)}</span></div>}
                {(p.loan_balance || 0) > 0 && (
                  <div className="p-2 bg-warning/10 rounded space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-xs">Loan Balance</span>
                      <span className="text-sm font-bold text-warning">{cur()} {p.loan_balance.toFixed(2)}</span>
                    </div>
                    {/* iter861 — Auto-deduct toggle + monthly installment inline editor */}
                    <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-warning/20">
                      <label className="flex items-center gap-1 text-[11px] cursor-pointer" data-testid={`partner-autodeduct-${p.id}`}>
                        <input
                          type="checkbox"
                          checked={!!p.auto_deduct}
                          onChange={async (e) => {
                            try {
                              await api.patch(`/partners/${p.id}/auto-deduct`, { auto_deduct: e.target.checked, monthly_installment: p.monthly_installment || 0 });
                              toast.success(e.target.checked ? 'Auto-deduct enabled' : 'Auto-deduct disabled');
                              fetchData();
                            } catch { toast.error('Toggle failed'); }
                          }}
                        />
                        Auto-deduct from salary
                      </label>
                      {p.auto_deduct && (
                        <input
                          type="number"
                          step="0.01"
                          defaultValue={p.monthly_installment || 0}
                          onBlur={async (e) => {
                            const v = Number(e.target.value || 0);
                            if (v === (p.monthly_installment || 0)) return;
                            try {
                              await api.patch(`/partners/${p.id}/auto-deduct`, { auto_deduct: true, monthly_installment: v });
                              toast.success(`Installment set to ${cur()} ${v.toFixed(2)}`);
                              fetchData();
                            } catch { toast.error('Save failed'); }
                          }}
                          placeholder="Monthly SAR"
                          className="h-6 w-24 rounded border border-warning/30 bg-background px-2 text-[11px]"
                          data-testid={`partner-installment-${p.id}`}
                        />
                      )}
                      {/* Export ledger buttons */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px] ml-auto"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/deduction-ledger/partner/${p.id}.csv`, { responseType: 'blob' });
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement('a'); a.href = url; a.download = `deduction-ledger-${p.name}.csv`; a.click();
                            URL.revokeObjectURL(url);
                          } catch { toast.error('Export failed'); }
                        }}
                        data-testid={`partner-ledger-csv-${p.id}`}
                        title="Download loan-repayment ledger (CSV)"
                      >Ledger CSV</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-[10px]"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/deduction-ledger/partner/${p.id}.pdf`, { responseType: 'blob' });
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement('a'); a.href = url; a.download = `deduction-ledger-${p.name}.pdf`; a.click();
                            URL.revokeObjectURL(url);
                          } catch { toast.error('Export failed'); }
                        }}
                        data-testid={`partner-ledger-pdf-${p.id}`}
                        title="Download loan-repayment ledger (PDF)"
                      >PDF</Button>
                    </div>
                  </div>
                )}
                {(p.total_salary_paid || 0) > 0 && <div className="flex justify-between p-2 bg-orange-50 dark:bg-orange-950/20 rounded"><span className="text-xs flex items-center gap-1"><DollarSign size={12} />Salary Paid (Total)</span><span className="text-sm font-bold text-orange-700 dark:text-orange-400">{cur()} {p.total_salary_paid.toFixed(2)}</span></div>}
                {(p.total_advances_paid || 0) > 0 && <div className="flex justify-between p-2 bg-indigo-50 dark:bg-indigo-950/20 rounded"><span className="text-xs flex items-center gap-1"><TrendingDown size={12} />Advances Paid</span><span className="text-sm font-bold text-indigo-700 dark:text-indigo-400">{cur()} {p.total_advances_paid.toFixed(2)}</span></div>}
                {((p.total_invested || 0) > 0 || (p.total_withdrawn || 0) > 0) ? (
                  <>
                    <div className="flex justify-between p-2 bg-success/10 rounded"><span className="text-xs flex items-center gap-1"><TrendingUp size={12} />Capital Invested</span><span className="text-sm font-bold text-success">{cur()} {(p.total_invested || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between p-2 bg-error/10 rounded"><span className="text-xs flex items-center gap-1"><TrendingDown size={12} />Capital Withdrawn</span><span className="text-sm font-bold text-error">{cur()} {(p.total_withdrawn || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between p-2 bg-primary/10 rounded border border-primary/20"><span className="text-xs font-medium">Capital Balance</span><span className={`text-sm font-bold ${(p.balance || 0) >= 0 ? 'text-success' : 'text-error'}`}>{cur()} {(p.balance || 0).toFixed(2)}</span></div>
                  </>
                ) : (
                  <div className="text-[11px] text-muted-foreground italic px-1 pt-1">No capital movements — use <span className="font-medium">Investment / Withdrawal</span> to record opening capital.</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredTxns.length > 0 && (
          <Card className="border-border"><CardHeader><CardTitle className="font-outfit text-base">Recent Transactions</CardTitle></CardHeader><CardContent>
            <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b"><th className="text-left p-3 text-sm font-medium">Date</th><th className="text-left p-3 text-sm font-medium">Partner</th><th className="text-left p-3 text-sm font-medium hidden sm:table-cell">Type</th><th className="text-left p-3 text-sm font-medium hidden sm:table-cell">Period</th><th className="text-left p-3 text-sm font-medium hidden md:table-cell">Description</th><th className="text-left p-3 text-sm font-medium hidden sm:table-cell">Mode</th><th className="text-right p-3 text-sm font-medium">Amount</th><th className="text-right p-3 text-sm font-medium">Actions</th></tr></thead>
            <tbody>{filteredTxns.slice(0, txnVisible).map(t => {
              const tc = TXN_TYPES.find(x => x.value === t.transaction_type);
              const isIn = t.transaction_type === 'investment';
              // Extract period — stored directly OR parse from description "Salary - Feb 2026"
              const periodDisplay = t.period || (() => {
                const m = (t.description || '').match(/-\s*([A-Z][a-z]{2}\s+\d{4})$/);
                return m ? m[1] : null;
              })();
              // Legacy-duplicate detection: same partner + same transaction_type + same period
              const isDup = t.transaction_type === 'salary' && periodDisplay && filteredTxns.filter(x =>
                x.partner_id === t.partner_id &&
                x.transaction_type === 'salary' &&
                (x.period === periodDisplay || (x.description || '').includes(periodDisplay))
              ).length > 1;
              return (<tr key={t.id} className={`border-b hover:bg-muted/50 ${isDup ? 'bg-error/5' : ''}`} data-testid={`partner-txn-row-${t.id}`}>
                <td className="p-3 text-sm">{format(new Date(t.date), 'MMM dd, yyyy')}</td>
                <td className="p-3 text-sm font-medium">
                  <div className="flex items-center gap-2">
                    {t.partner_name}
                    {isDup && <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-error text-white animate-pulse" title="Another salary payment exists for this partner + period" data-testid="partner-txn-duplicate-badge">⚠ DUPLICATE</span>}
                  </div>
                </td>
                <td className="p-3 hidden sm:table-cell"><Badge className={tc?.color || ''}>{tc?.label || t.transaction_type}</Badge></td>
                <td className="p-3 text-sm hidden sm:table-cell">{periodDisplay ? <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-info/10 text-info border border-info/30">📅 {periodDisplay}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="p-3 text-sm hidden md:table-cell">{t.description || '-'}</td>
                <td className="p-3 hidden sm:table-cell"><Badge variant="secondary" className="capitalize">{t.payment_mode}</Badge></td>
                <td className={`p-3 text-sm text-right font-bold ${isIn ? 'text-success' : 'text-error'}`}>{isIn ? '+' : '-'}SR {t.amount.toFixed(2)}</td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditTxn({ id: t.id, amount: String(t.amount), date: String(t.date).slice(0,10), payment_mode: t.payment_mode || 'cash', description: t.description || '' }); }} className="h-7" data-testid={`edit-txn-${t.id}`}><Edit size={12} /></Button>
                    <Button size="sm" variant="ghost" onClick={async () => { if (window.confirm('Delete?')) { await api.delete(`/partner-transactions/${t.id}`); fetchData(); } }} className="h-7 text-error"><Trash2 size={12} /></Button>
                  </div>
                </td>
              </tr>);
            })}</tbody></table></div>
            {filteredTxns.length > txnVisible && (
              <div className="text-center mt-3">
                <Button variant="outline" className="rounded-xl" onClick={() => setTxnVisible(c => c + 50)} data-testid="load-more-partner-txns-btn">
                  Load More ({Math.min(txnVisible, filteredTxns.length)} of {filteredTxns.length})
                </Button>
                <Button variant="ghost" className="rounded-xl ml-2" onClick={() => setTxnVisible(filteredTxns.length)} data-testid="show-all-partner-txns-btn">
                  Show All
                </Button>
              </div>
            )}
          </CardContent></Card>
        )}
        {/* Edit Partner Transaction Dialog (iter753) */}
        <Dialog open={!!editTxn} onOpenChange={(o) => { if (!o) setEditTxn(null); }}>
          <DialogContent data-testid="edit-txn-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Correct Transaction</DialogTitle></DialogHeader>
            {editTxn && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Amount *</Label><Input type="number" step="0.01" value={editTxn.amount} onChange={(e) => setEditTxn({ ...editTxn, amount: e.target.value })} data-testid="edit-txn-amount" /></div>
                  <div><Label>Date</Label><Input type="date" value={editTxn.date} onChange={(e) => setEditTxn({ ...editTxn, date: e.target.value })} data-testid="edit-txn-date" /></div>
                  <div><Label>Mode</Label><Select value={editTxn.payment_mode} onValueChange={(v) => setEditTxn({ ...editTxn, payment_mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent></Select></div>
                </div>
                <div><Label>Description</Label><Input value={editTxn.description} onChange={(e) => setEditTxn({ ...editTxn, description: e.target.value })} /></div>
                <p className="text-[11px] text-muted-foreground">Correcting amount/date also updates the linked expense and re-posts the journal entry.</p>
                <Button onClick={saveEditTxn} className="w-full" data-testid="edit-txn-save">Save Correction</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Pay Partner Salary Dialog */}
        <Dialog open={showPaySalaryDialog} onOpenChange={(o) => { setShowPaySalaryDialog(o); if (!o) setConfirmOverpay(false); }}>
          <DialogContent><DialogHeader><DialogTitle className="font-outfit">Partner Payment - {payingPartner?.name}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Salary: {cur()} {payingPartner?.salary?.toFixed(2)}{(payingPartner?.loan_balance || 0) > 0 ? ` | Loan: ${cur()} ${payingPartner.loan_balance.toFixed(2)}` : ''}</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Type</Label><Select value={salaryPayData.type} onValueChange={(v) => setSalaryPayData({...salaryPayData, type: v})}><SelectTrigger data-testid="partner-pay-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="salary">Salary</SelectItem><SelectItem value="advance">Advance/Loan</SelectItem><SelectItem value="loan_repayment">Loan Repayment</SelectItem><SelectItem value="id_card">ID / Iqama</SelectItem><SelectItem value="visa">Visa</SelectItem><SelectItem value="government_fees">Government Fees</SelectItem></SelectContent></Select></div>
                <div><Label>Amount *</Label><Input type="number" step="0.01" value={salaryPayData.amount} onChange={(e) => setSalaryPayData({...salaryPayData, amount: e.target.value})} /></div>
                <div><Label>Period (label only)</Label>
                  <div className="flex gap-1">
                    <Select value={String(salaryPayData.period_month)} onValueChange={(v) => setSalaryPayData({...salaryPayData, period_month: parseInt(v)})}>
                      <SelectTrigger data-testid="partner-period-month"><SelectValue /></SelectTrigger>
                      <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={String(salaryPayData.period_year)} onValueChange={(v) => setSalaryPayData({...salaryPayData, period_year: parseInt(v)})}>
                      <SelectTrigger className="w-24" data-testid="partner-period-year"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({length: (new Date().getFullYear() + 2) - 2018 + 1}, (_, k) => 2018 + k).map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Work month this payment covers</p>
                </div>
                <div><Label>Paid on Date <span className="text-[10px] text-muted-foreground font-normal">(drives the expense month)</span></Label><Input type="date" value={salaryPayData.date} onChange={(e) => setSalaryPayData({...salaryPayData, date: e.target.value})} data-testid="salary-expense-date" /></div>
                <div><Label>Mode</Label><Select value={salaryPayData.payment_mode} onValueChange={(v) => setSalaryPayData({...salaryPayData, payment_mode: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent></Select></div>
                <div><Label>Branch</Label><Select value={salaryPayData.branch_id || "none"} onValueChange={(v) => setSalaryPayData({...salaryPayData, branch_id: v === "none" ? "" : v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Company</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
              </div>
              {['id_card', 'visa', 'government_fees'].includes(salaryPayData.type) && (
                <p className="text-xs text-warning" data-testid="partner-fee-expense-hint">Recorded as an expense linked to this partner.</p>
              )}
              <div>
                <Label>Comments / Notes</Label>
                <Textarea
                  rows={2}
                  placeholder="Any remarks about this payment (optional)"
                  value={salaryPayData.comments}
                  onChange={(e) => setSalaryPayData({...salaryPayData, comments: e.target.value})}
                  data-testid="partner-payment-comments"
                />
              </div>
              {partnerAlerts.length > 0 && (
                <div className="space-y-1.5" data-testid="partner-salary-alerts">
                  {partnerAlerts.map((a, i) => (
                    <div key={i} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[12px] border ${
                      a.level === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : a.level === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid={`partner-salary-alert-${i}`}>
                      <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span>{a.message}</span>
                    </div>
                  ))}
                </div>
              )}
              {salaryPayData.type === 'salary' && (
                <SalaryDeductionCard
                  deductions={partnerDeductions}
                  cur={cur()}
                  onApply={(rec) => {
                    const sal = parseFloat(payingPartner?.salary) || 0;
                    const net = partnerDeductions?.remaining_after_deduction != null
                      ? partnerDeductions.remaining_after_deduction
                      : Math.max(0, (parseFloat(salaryPayData.amount) || sal) - rec);
                    setSalaryPayData(d => ({ ...d, amount: String(Math.max(0, Number(net) || 0)) }));
                    toast.success(`Deduction applied — paying ${cur()} ${(net || 0).toFixed(2)} after ${cur()} ${rec.toFixed(2)} deduction`);
                  }}
                />
              )}
              {salaryPayData.type === 'salary' && (
                <label className="flex items-start gap-2 text-xs text-amber-700 cursor-pointer rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="confirm-overpayment-checkbox">
                  <input type="checkbox" checked={confirmOverpay} onChange={(e) => setConfirmOverpay(e.target.checked)} className="h-3.5 w-3.5 mt-0.5 accent-amber-600" />
                  <span><span className="font-semibold">Confirm Overpayment</span> — allow paying more than the remaining salary for this period</span>
                </label>
              )}
              <Button className="rounded-xl" onClick={async () => {
                const basePayload = {
                  ...salaryPayData,
                  period: periodString(salaryPayData.period_month, salaryPayData.period_year),
                  amount: parseFloat(salaryPayData.amount),
                  branch_id: salaryPayData.branch_id || null,
                  date: `${salaryPayData.date}T12:00:00`,
                  comments: salaryPayData.comments || '',
                  allow_overpayment: confirmOverpay,
                };
                const postPay = async (allowDup) => {
                  const res = await api.post(`/partners/${payingPartner.id}/pay-salary`, { ...basePayload, allow_duplicate: !!allowDup });
                  toast.success(res.data.message || 'Payment recorded');
                  setShowPaySalaryDialog(false);
                  fetchData();
                };
                try { await postPay(false); }
                catch (err) {
                  if (err?.response?.status === 409) {
                    if (window.confirm(`${err.response?.data?.detail || 'Already paid.'}\n\nRecord anyway?`)) {
                      try { await postPay(true); }
                      catch (e2) { toast.error(e2.response?.data?.detail || 'Failed'); }
                    }
                  } else {
                    toast.error(err.response?.data?.detail || 'Failed');
                  }
                }
              }}>Record Payment</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pay All Salaries — bulk dialog */}
        <Dialog open={showPayAllDialog} onOpenChange={setShowPayAllDialog}>
          <DialogContent className="max-w-2xl" data-testid="pay-all-salaries-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2"><DollarSign size={18} className="text-orange-600" /> Pay All Salaries</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Period Month</Label>
                  <Select
                    value={String(payAllSettings.period_month)}
                    onValueChange={(v) => setPayAllSettings({ ...payAllSettings, period_month: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="pay-all-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={m} value={String(i)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Period Year</Label>
                  <Input
                    type="number" min="2018" max="2099"
                    value={payAllSettings.period_year}
                    onChange={(e) => setPayAllSettings({ ...payAllSettings, period_year: parseInt(e.target.value || '0') })}
                    data-testid="pay-all-year"
                  />
                </div>
                <div>
                  <Label className="text-xs">Payment Mode</Label>
                  <Select
                    value={payAllSettings.payment_mode}
                    onValueChange={(v) => setPayAllSettings({ ...payAllSettings, payment_mode: v })}
                  >
                    <SelectTrigger data-testid="pay-all-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Paid On</Label>
                  <Input
                    type="date" value={payAllSettings.date}
                    onChange={(e) => setPayAllSettings({ ...payAllSettings, date: e.target.value })}
                    data-testid="pay-all-date"
                  />
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto border border-border rounded-xl p-2 space-y-1">
                {Object.keys(payAllRows).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">No partners have a monthly salary set</p>
                ) : partners.filter(p => payAllRows[p.id]).map(p => {
                  const row = payAllRows[p.id];
                  return (
                    <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50" data-testid={`pay-all-row-${p.id}`}>
                      <input
                        type="checkbox" checked={row.include}
                        onChange={(e) => setPayAllRows({ ...payAllRows, [p.id]: { ...row, include: e.target.checked } })}
                        className="w-4 h-4 accent-orange-500"
                        data-testid={`pay-all-include-${p.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-[10px] text-muted-foreground">Default: {cur()} {(p.salary || 0).toFixed(2)}</p>
                      </div>
                      <Input
                        type="number" step="0.01" className="w-32 text-sm" disabled={!row.include}
                        value={row.amount}
                        onChange={(e) => setPayAllRows({ ...payAllRows, [p.id]: { ...row, amount: e.target.value } })}
                        data-testid={`pay-all-amount-${p.id}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">Total this run: </span>
                  <span className="font-bold text-orange-600">
                    {cur()} {Object.entries(payAllRows)
                      .filter(([, r]) => r.include)
                      .reduce((s, [, r]) => s + (parseFloat(r.amount) || 0), 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowPayAllDialog(false)}>Cancel</Button>
                  <Button
                    onClick={submitPayAll} disabled={payAllLoading}
                    className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="pay-all-submit-btn"
                  >
                    {payAllLoading ? 'Paying…' : 'Pay All'}
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Partners already paid for this period will be skipped automatically.
                Use the individual <span className="font-medium">Pay</span> button with override if you really need to re-pay someone.
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Partner Statement of Account Dialog */}
        <Dialog open={showStatementDialog} onOpenChange={(o) => { setShowStatementDialog(o); if (!o) { setStmtPartner(null); setStmtPreview(null); } }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-outfit flex items-center gap-2"><FileText size={16} /> Statement of Account — {stmtPartner?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From</Label>
                  <Input type="date" value={stmtRange.start_date} onChange={(e) => { setStmtRange({ ...stmtRange, start_date: e.target.value }); setStmtPreview(null); }} data-testid="stmt-start-date" min="2018-01-01" />
                </div>
                <div>
                  <Label>To</Label>
                  <Input type="date" value={stmtRange.end_date} onChange={(e) => { setStmtRange({ ...stmtRange, end_date: e.target.value }); setStmtPreview(null); }} data-testid="stmt-end-date" min="2018-01-01" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[_currentYear, _currentYear - 1, _currentYear - 2].map(y => (
                  <Button key={y} type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setStmtRange({ start_date: `${y}-01-01`, end_date: `${y}-12-31` }); setStmtPreview(null); }}>FY {y}</Button>
                ))}
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setStmtRange({ start_date: '2018-01-01', end_date: `${_currentYear}-12-31` }); setStmtPreview(null); }}>All-time</Button>
              </div>

              {stmtPreview && (
                <div className="rounded-xl border border-border p-4 bg-gradient-to-br from-stone-50 to-white">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Opening</div>
                      <div className="text-sm font-bold text-foreground">{cur()} {stmtPreview.capital_account.opening_balance.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Net Change</div>
                      <div className={`text-sm font-bold ${stmtPreview.capital_account.net_change >= 0 ? 'text-success' : 'text-error'}`}>{cur()} {stmtPreview.capital_account.net_change.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Closing Capital</div>
                      <div className={`text-sm font-bold ${stmtPreview.capital_account.closing_balance >= 0 ? 'text-success' : 'text-error'}`}>{cur()} {stmtPreview.capital_account.closing_balance.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Net Paid (Ops)</div>
                      <div className="text-sm font-bold text-orange-600">{cur()} {stmtPreview.operating_account.net_paid.toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                    <div>Capital rows: <span className="font-semibold text-foreground">{stmtPreview.capital_account.rows.length}</span></div>
                    <div>Operating rows: <span className="font-semibold text-foreground">{stmtPreview.operating_account.rows.length}</span></div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={fetchStatement} disabled={stmtLoading} data-testid="stmt-preview-btn">
                  {stmtLoading ? 'Loading…' : (stmtPreview ? 'Refresh' : 'Preview')}
                </Button>
                <Button type="button" className="rounded-xl bg-primary text-white" onClick={downloadStatementPDF} disabled={stmtLoading} data-testid="stmt-download-btn">
                  <Download size={14} className="mr-1" /> Download PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pay from Bank — unified dialog seeded with this partner. */}
        <BankPaymentDialog
          open={showBankPay}
          onOpenChange={setShowBankPay}
          seedPayee={bankPaySeed}
          onCreated={() => { setShowBankPay(false); setBankPaySeed(null); fetchData(); toast.success('Bank payment recorded'); }}
        />

        {/* Per-partner Salary Status (month-by-month, full year) */}
        <SalaryStatusDialog
          open={!!statusPartner}
          onOpenChange={(o) => { if (!o) setStatusPartner(null); }}
          entity={statusPartner ? { id: statusPartner.id, name: statusPartner.name, type: 'partner' } : null}
        />
      </div>
    </DashboardLayout>
  );
}
